import React, { useState, useRef, useEffect } from 'react';
import { Booking, Expense, Insight, ServiceType, ExpenseCategory } from '../types';
import { getBusinessSettings } from '../utils/businessSettings';
import { logAuditAction } from '../utils/auditLogger';
import { recordBackupAction, getBackupStatus, BackupStatus } from '../utils/backupManager';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  FileText, 
  Check, 
  AlertCircle, 
  X, 
  ArrowDownCircle, 
  Settings,
  Brain,
  Calendar,
  DollarSign,
  TrendingUp,
  Info,
  ChevronDown,
  Receipt,
  Percent,
  Database,
  FileJson,
  ShieldCheck,
  ShieldAlert,
  Sparkles
} from 'lucide-react';

// Basic standard CSV parser that correctly handles quoted fields with commas
export function parseCSV(text: string): string[][] {
  const lines: string[][] = [];
  let row: string[] = [];
  let inQuotes = false;
  let currentValue = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === ',' || char === ';') && !inQuotes) {
      row.push(currentValue.trim());
      currentValue = '';
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
      row.push(currentValue.trim());
      if (row.length > 0 && row.some(cell => cell !== '')) {
        lines.push(row);
      }
      row = [];
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  if (currentValue || row.length > 0) {
    row.push(currentValue.trim());
    if (row.some(cell => cell !== '')) {
      lines.push(row);
    }
  }
  return lines;
}

// Generate CSV string from array of objects
export function generateCSV<T extends object>(data: T[], headers: (keyof T)[], headerLabels: string[]): string {
  const headerRow = headerLabels.map(label => `"${label.replace(/"/g, '""')}"`).join(',');
  const rows = data.map(row => {
    return headers.map(key => {
      const val = row[key];
      if (val === undefined || val === null) return '""';
      const str = String(val);
      return `"${str.replace(/"/g, '""')}"`;
    }).join(',');
  });
  return [headerRow, ...rows].join('\n');
}

// Trigger browser download for a text file
export function downloadFile(content: string, filename: string, contentType: string) {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Interactive Import Modal Component
interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'bookings' | 'expenses';
  streams: ServiceType[];
  onImportComplete: (importedData: any[], newStreamsAdded: string[]) => void;
}

export function CSVImportModal({ isOpen, onClose, type, streams, onImportComplete }: CSVImportModalProps) {
  const [dragActive, setDragActive] = useState(false);
  const [csvRawLines, setCsvRawLines] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappedFields, setMappedFields] = useState<Record<string, number>>({});
  const [parsedItems, setParsedItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1); // 1 = Upload, 2 = Mapping & Review
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Field specs for mapping
  const fieldSpecs = type === 'bookings' ? [
    { key: 'client_name', label: 'Client Name *', keywords: ['client', 'name', 'customer', 'who', 'person'] },
    { key: 'price', label: 'Price (Income) *', keywords: ['price', 'amount', 'income', 'value', 'revenue', 'cost'] },
    { key: 'type', label: 'Income Stream Type', keywords: ['stream', 'type', 'service', 'category'] },
    { key: 'date', label: 'Date', keywords: ['date', 'time', 'created', 'day'] }
  ] : [
    { key: 'note', label: 'Expense Description *', keywords: ['note', 'description', 'item', 'memo', 'title', 'what'] },
    { key: 'amount', label: 'Amount *', keywords: ['amount', 'price', 'cost', 'expense', 'value'] },
    { key: 'category', label: 'Category', keywords: ['category', 'cat', 'group'] },
    { key: 'linked_stream', label: 'Linked Stream', keywords: ['stream', 'type', 'service', 'link', 'linked'] },
    { key: 'date', label: 'Date', keywords: ['date', 'time', 'created', 'day'] }
  ];

  const resetState = () => {
    setCsvRawLines([]);
    setHeaders([]);
    setMappedFields({});
    setParsedItems([]);
    setError(null);
    setStep(1);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a valid CSV file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) {
        setError('Empty file loaded.');
        return;
      }
      try {
        const rows = parseCSV(text);
        if (rows.length < 2) {
          setError('CSV file must have a header row and at least one data row.');
          return;
        }

        const detectedHeaders = rows[0];
        setHeaders(detectedHeaders);
        setCsvRawLines(rows.slice(1));

        // Auto-map headers
        const initialMap: Record<string, number> = {};
        fieldSpecs.forEach(spec => {
          const matchIndex = detectedHeaders.findIndex(header => {
            const h = header.toLowerCase();
            return spec.keywords.some(keyword => h.includes(keyword));
          });
          if (matchIndex !== -1) {
            initialMap[spec.key] = matchIndex;
          }
        });
        setMappedFields(initialMap);
        setError(null);
        setStep(2);
      } catch (err) {
        setError('Could not parse CSV. Please make sure the format is correct.');
      }
    };
    reader.readAsText(file);
  };

  const handleMapChange = (fieldKey: string, colIndex: number) => {
    setMappedFields(prev => ({
      ...prev,
      [fieldKey]: colIndex
    }));
  };

  // Run the parsed item extraction
  const handleImportSave = () => {
    // Validate required mappings
    const required = type === 'bookings' ? ['client_name', 'price'] : ['note', 'amount'];
    const missing = required.filter(req => mappedFields[req] === undefined || mappedFields[req] === -1);
    
    if (missing.length > 0) {
      setError(`Please map the required fields: ${missing.map(m => m.replace('_', ' ')).join(', ')}`);
      return;
    }

    const imported: any[] = [];
    const newStreamsSet = new Set<string>();

    csvRawLines.forEach((row, rowIndex) => {
      if (row.length === 0 || row.every(cell => cell === '')) return;

      const getVal = (fieldKey: string) => {
        const colIdx = mappedFields[fieldKey];
        if (colIdx === undefined || colIdx === -1) return '';
        return row[colIdx] || '';
      };

      if (type === 'bookings') {
        const client_name = getVal('client_name') || `Client #${rowIndex + 1}`;
        const priceRaw = parseFloat(getVal('price').replace(/[^0-9.-]/g, ''));
        const price = isNaN(priceRaw) ? 0 : priceRaw;
        
        let streamType = getVal('type').trim().toLowerCase();
        // If stream empty or not specified, map to default first stream, or "consult"
        if (!streamType) {
          streamType = streams[0] || 'consult';
        }

        // Auto-detect stream additions
        if (streamType && !streams.includes(streamType)) {
          newStreamsSet.add(streamType);
        }

        const dateRaw = getVal('date');
        const date = dateRaw ? new Date(dateRaw).toISOString() : new Date().toISOString();

        imported.push({
          client_name,
          price,
          type: streamType,
          date
        });
      } else {
        const note = getVal('note') || `Expense #${rowIndex + 1}`;
        const amountRaw = parseFloat(getVal('amount').replace(/[^0-9.-]/g, ''));
        const amount = isNaN(amountRaw) ? 0 : amountRaw;
        
        let categoryRaw = getVal('category').trim().toLowerCase() as ExpenseCategory;
        const validCategories: ExpenseCategory[] = ['salary', 'travel', 'supplies', 'rent', 'other'];
        if (!validCategories.includes(categoryRaw)) {
          categoryRaw = categoryRaw.includes('salary') || categoryRaw.includes('pay') || categoryRaw.includes('wage') ? 'salary' : 'supplies';
        }

        let linked_stream = getVal('linked_stream').trim().toLowerCase();
        if (!linked_stream) {
          linked_stream = streams[0] || 'consult';
        }

        if (linked_stream && !streams.includes(linked_stream)) {
          newStreamsSet.add(linked_stream);
        }

        const dateRaw = getVal('date');
        const date = dateRaw ? new Date(dateRaw).toISOString() : new Date().toISOString();

        imported.push({
          note,
          amount,
          category: categoryRaw,
          linked_stream,
          date
        });
      }
    });

    onImportComplete(imported, Array.from(newStreamsSet));
    resetState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#1F2426]">
          <div className="flex items-center gap-2.5">
            <span className="p-1.5 bg-[#14B8A6]/10 rounded-lg text-[#14B8A6]">
              <Upload size={18} />
            </span>
            <div>
              <h3 className="text-base font-semibold text-white">
                Bulk Import {type === 'bookings' ? 'Bookings' : 'Expenses'}
              </h3>
              <p className="text-[11px] text-gray-500 font-medium">Quickly populate your sandbox via historical CSV files</p>
            </div>
          </div>
          <button 
            onClick={() => { resetState(); onClose(); }} 
            className="text-gray-400 hover:text-white transition p-1 hover:bg-white/5 rounded-lg cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-xl p-4 flex items-start gap-3 text-xs animate-fade-in">
              <AlertCircle size={16} className="shrink-0 text-rose-400 mt-0.5" />
              <div>
                <span className="font-bold">Error Processing:</span> {error}
              </div>
            </div>
          )}

          {step === 1 ? (
            <div 
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-10 text-center flex flex-col items-center justify-center gap-4 cursor-pointer transition-all ${
                dragActive 
                  ? 'border-[#14B8A6] bg-[#14B8A6]/5' 
                  : 'border-[#2A2F31] hover:border-[#14B8A6]/40 hover:bg-white/[0.01]'
              }`}
            >
              <input 
                ref={fileInputRef} 
                type="file" 
                accept=".csv" 
                onChange={handleFileChange} 
                className="hidden" 
              />
              <div className="w-12 h-12 bg-white/5 border border-[#2A2F31] rounded-xl flex items-center justify-center text-gray-400">
                <FileSpreadsheet size={24} className="text-[#14B8A6]" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-white">Drag & drop your CSV file here</p>
                <p className="text-xs text-gray-400">or click to browse local files</p>
              </div>
              <div className="pt-2 text-[10px] text-gray-500 max-w-md">
                CSV should have simple header columns. For {type === 'bookings' ? 'bookings' : 'expenses'}: we automatically match headings like Client Name, Cost/Price, Income Stream, and Date.
              </div>
            </div>
          ) : (
            <div className="space-y-5 animate-fade-in">
              {/* Columns Mapping UI */}
              <div className="space-y-3 bg-[#0C0E0F] border border-[#1F2426] rounded-xl p-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Settings size={12} /> Map CSV Headers to Fields
                </h4>
                <p className="text-[11px] text-gray-500 leading-normal">
                  Our algorithm auto-guessed these columns. Verify or change below to ensure accurate records:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                  {fieldSpecs.map(spec => {
                    const mappedIdx = mappedFields[spec.key] ?? -1;
                    return (
                      <div key={spec.key} className="space-y-1.5">
                        <label className="text-[11px] font-semibold text-gray-300 block">
                          {spec.label}
                        </label>
                        <div className="relative">
                          <select
                            value={mappedIdx}
                            onChange={(e) => handleMapChange(spec.key, parseInt(e.target.value))}
                            className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] appearance-none cursor-pointer"
                          >
                            <option value={-1}>-- Ignore / Skip --</option>
                            {headers.map((h, i) => (
                              <option key={i} value={i}>
                                Column {i + 1}: "{h}"
                              </option>
                            ))}
                          </select>
                          <ChevronDown size={14} className="absolute right-3.5 top-3.5 text-gray-500 pointer-events-none" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Data Preview */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Raw Records Preview ({csvRawLines.length} rows detected)
                </h4>
                <div className="border border-[#1F2426] rounded-xl overflow-hidden max-h-48 overflow-y-auto bg-[#0C0E0F]">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-[#151819] border-b border-[#1F2426]">
                        {headers.map((h, i) => (
                          <th key={i} className="p-2.5 font-bold text-gray-400 truncate max-w-[120px]">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1F2426]/40 text-gray-400 font-mono text-[10px]">
                      {csvRawLines.slice(0, 5).map((row, rIdx) => (
                        <tr key={rIdx}>
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className="p-2.5 max-w-[150px] truncate">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvRawLines.length > 5 && (
                    <div className="p-2 bg-[#151819]/50 border-t border-[#1F2426] text-center text-[10px] text-gray-500">
                      Showing top 5 of {csvRawLines.length} records
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4.5 border-t border-[#1F2426] bg-[#0C0E0F]/40 gap-4">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-4.5 h-10.5 border border-[#2A2F31] hover:text-white rounded-xl text-xs text-gray-400 font-semibold transition active:scale-95 cursor-pointer"
            >
              Change File
            </button>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={() => { resetState(); onClose(); }}
              className="px-4.5 h-10.5 border border-[#2A2F31] hover:text-white rounded-xl text-xs text-gray-400 font-semibold transition active:scale-95 cursor-pointer"
            >
              Cancel
            </button>
            {step === 2 && (
              <button
                onClick={handleImportSave}
                className="px-5 h-10.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-semibold rounded-xl text-xs shadow-md transition active:scale-95 cursor-pointer flex items-center gap-1.5"
              >
                <Check size={14} className="stroke-[3px]" /> Complete Import
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Download Center Component
interface ExportCenterProps {
  bookings: Booking[];
  expenses: Expense[];
  insights: Insight[];
  streams: ServiceType[];
  threshold: number;
  onOpenReconciler?: () => void;
}

function ExportCenterComponent({ bookings, expenses, insights, streams, threshold, onOpenReconciler }: ExportCenterProps) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [backupStatus, setBackupStatus] = useState<BackupStatus>(() => getBackupStatus(7));

  useEffect(() => {
    const handleUpdate = () => {
      setBackupStatus(getBackupStatus(7));
    };
    window.addEventListener('solostack_backup_updated', handleUpdate);
    return () => window.removeEventListener('solostack_backup_updated', handleUpdate);
  }, []);

  const exportBookingsToCSV = () => {
    setDownloading('bookings_csv');
    recordBackupAction();
    const headers: (keyof Booking)[] = ['client_name', 'price', 'type', 'date', 'created_at'];
    const labels = ['Client Name', 'Price (R)', 'Income Stream Type', 'Date', 'Created At'];
    const csvContent = generateCSV(bookings, headers, labels);
    
    setTimeout(() => {
      downloadFile(csvContent, `azuraos_bookings_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
      setDownloading(null);
    }, 400);
  };

  const exportExpensesToCSV = () => {
    setDownloading('expenses_csv');
    recordBackupAction();
    const headers: (keyof Expense)[] = ['note', 'amount', 'category', 'linked_stream', 'date', 'created_at'];
    const labels = ['Expense Description', 'Amount (R)', 'Category', 'Linked Income Stream', 'Date', 'Created At'];
    const csvContent = generateCSV(expenses, headers, labels);
    
    setTimeout(() => {
      downloadFile(csvContent, `azuraos_expenses_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
      setDownloading(null);
    }, 400);
  };

  const exportJLensToCSV = () => {
    setDownloading('jlens_csv');
    recordBackupAction();
    const headers: (keyof Insight)[] = ['title', 'note', 'linked_type', 'linked_id', 'created_at'];
    const labels = ['Insight Title', 'Reflection Note', 'Linked Item Type', 'Linked Item ID', 'Captured At'];
    const csvContent = generateCSV(insights, headers, labels);

    setTimeout(() => {
      downloadFile(csvContent, `azuraos_jlens_journal_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
      setDownloading(null);
    }, 400);
  };

  // Export full application state as JSON backup
  const exportFullBackupJSON = () => {
    setDownloading('full_backup_json');
    recordBackupAction();
    const settings = getBusinessSettings();
    const backupData = {
      appName: 'AzuraOS Sovereign Founder HQ',
      exportVersion: '1.0',
      exportedAt: new Date().toISOString(),
      businessSettings: settings,
      streams: streams,
      bookings: bookings,
      expenses: expenses,
      insights: insights,
      summary: {
        totalBookingsCount: bookings.length,
        totalExpensesCount: expenses.length,
        totalInsightsCount: insights.length,
        totalStreamsCount: streams.length,
        totalRevenue: bookings.reduce((sum, b) => sum + b.price, 0),
        totalExpenses: expenses.reduce((sum, e) => sum + e.amount, 0)
      }
    };

    const jsonStr = JSON.stringify(backupData, null, 2);
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `azuraos_full_workspace_backup_${dateStr}.json`;

    setTimeout(() => {
      downloadFile(jsonStr, filename, 'application/json');
      setDownloading(null);
    }, 400);
  };

  // Configurable VAT / Tax Summary CSV Export
  const exportTaxSummaryCSV = () => {
    setDownloading('tax_csv');
    recordBackupAction();
    const settings = getBusinessSettings();
    const dateStr = new Date().toISOString().split('T')[0];
    const rate = settings.vatRate !== undefined ? settings.vatRate : 15;
    const divisor = 1 + rate / 100;
    const cur = settings.currencySymbol || 'R';
    const taxLabel = settings.taxAuthorityLabel || 'Tax Authority';

    const activeBookings = bookings.filter(b => b.status !== 'cancelled');
    const totalGrossRev = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
    const totalNetRev = totalGrossRev / divisor;
    const totalOutputVat = totalGrossRev - totalNetRev;

    const totalGrossExp = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const totalNetExp = totalGrossExp / divisor;
    const totalInputVat = totalGrossExp - totalNetExp;

    const netVatPayable = totalOutputVat - totalInputVat;

    let csvContent = `${rate}% VAT / TAX FILING REPORT\n`;
    csvContent += `Entity Name,"${(settings.companyName || settings.senderName || 'Your Business Name').replace(/"/g, '""')}"\n`;
    csvContent += `Tax Reference Number,"${(settings.taxNumber || 'Unregistered / Standard Rate').replace(/"/g, '""')}"\n`;
    csvContent += `Tax Authority / Dept,"${taxLabel.replace(/"/g, '""')}"\n`;
    csvContent += `Report Generated Date,${dateStr}\n`;
    csvContent += `Applied Tax Rate,${rate.toFixed(2)}%\n\n`;

    csvContent += `=== ${rate}% TAX EXECUTIVE SUMMARY ===\n`;
    csvContent += `Tax Metric,Gross Amount (${cur}),Net Amount Excl. Tax (${cur}),${rate}% Tax Portion (${cur})\n`;
    csvContent += `Output Tax (Revenue / Bookings),${totalGrossRev.toFixed(2)},${totalNetRev.toFixed(2)},${totalOutputVat.toFixed(2)}\n`;
    csvContent += `Input Tax (Expenses Claimable),${totalGrossExp.toFixed(2)},${totalNetExp.toFixed(2)},${totalInputVat.toFixed(2)}\n`;
    csvContent += `NET TAX PAYABLE / (REFUNDABLE),${(totalGrossRev - totalGrossExp).toFixed(2)},${(totalNetRev - totalNetExp).toFixed(2)},${netVatPayable.toFixed(2)}\n\n`;

    csvContent += `=== OUTPUT TAX SCHEDULE - BOOKINGS (REVENUE) ===\n`;
    csvContent += `Client Name,Income Stream,Status,Booking Date,Gross Price (${cur}),Net Price Excl. Tax (${cur}),${rate}% Output Tax (${cur})\n`;
    activeBookings.forEach(b => {
      const gross = Number(b.price) || 0;
      const net = gross / divisor;
      const vat = gross - net;
      csvContent += `"${(b.client_name || 'Client').replace(/"/g, '""')}","${b.type}","${b.status || 'paid'}","${b.date.split('T')[0]}",${gross.toFixed(2)},${net.toFixed(2)},${vat.toFixed(2)}\n`;
    });

    csvContent += `\n=== INPUT TAX SCHEDULE - EXPENSES ===\n`;
    csvContent += `Description,Category,Linked Stream,Expense Date,Gross Amount (${cur}),Net Amount Excl. Tax (${cur}),${rate}% Input Tax (${cur})\n`;
    expenses.forEach(e => {
      const gross = e.amount;
      const net = gross / divisor;
      const vat = gross - net;
      csvContent += `"${(e.note || 'Expense').replace(/"/g, '""')}","${e.category}","${e.linked_stream}","${e.date.split('T')[0]}",${gross.toFixed(2)},${net.toFixed(2)},${vat.toFixed(2)}\n`;
    });

    setTimeout(() => {
      downloadFile(csvContent, `azuraos_tax_summary_${rate}pct_${dateStr}.csv`, 'text/csv');
      setDownloading(null);
    }, 400);
  };

  // Generate printable Tax Report for official tax filing
  const handlePrintTaxPDFSummary = () => {
    setDownloading('tax_pdf');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Could not open print window. Please allow popups for AzuraOS.');
      setDownloading(null);
      return;
    }

    const settings = getBusinessSettings();
    const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const rate = settings.vatRate !== undefined ? settings.vatRate : 15;
    const divisor = 1 + rate / 100;
    const cur = settings.currencySymbol || 'R';
    const taxLabel = settings.taxAuthorityLabel || 'Tax Authority';

    const activeBookings = bookings.filter(b => b.status !== 'cancelled');
    const totalGrossRev = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
    const totalNetRev = totalGrossRev / divisor;
    const totalOutputVat = totalGrossRev - totalNetRev;

    const totalGrossExp = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const totalNetExp = totalGrossExp / divisor;
    const totalInputVat = totalGrossExp - totalNetExp;

    const netVatPayable = totalOutputVat - totalInputVat;

    // Stream Output VAT Breakdown
    const streamTaxRows = streams.map(stream => {
      const inc = activeBookings.filter(b => b.type === stream).reduce((sum, b) => sum + (Number(b.price) || 0), 0);
      const net = inc / divisor;
      const vat = inc - net;
      return `
        <tr>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-weight: 600; text-transform: capitalize;">${stream}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0;">${cur}${inc.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0;">${cur}${net.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-weight: 700; color: #15803D;">${cur}${vat.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    // Expense Category Input VAT Breakdown
    const categories: ExpenseCategory[] = ['travel', 'supplies', 'rent', 'other'];
    const categoryTaxRows = categories.map(cat => {
      const exp = expenses.filter(e => e.category === cat).reduce((sum, e) => sum + e.amount, 0);
      const net = exp / divisor;
      const vat = exp - net;
      return `
        <tr>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-weight: 600; text-transform: capitalize;">${cat}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0;">${cur}${exp.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0;">${cur}${net.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-weight: 700; color: #B91C1C;">${cur}${vat.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    // Transaction detail rows
    const bookingDetailRows = activeBookings.slice(0, 30).map(b => {
      const gross = Number(b.price) || 0;
      const net = gross / divisor;
      const vat = gross - net;
      return `
        <tr>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${b.client_name || 'Client'}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px; text-transform: capitalize;">${b.type}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${b.date.split('T')[0]}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${cur}${gross.toFixed(2)}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${cur}${net.toFixed(2)}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px; font-weight: bold; color: #15803D;">${cur}${vat.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    const expenseDetailRows = expenses.slice(0, 30).map(e => {
      const gross = e.amount;
      const net = gross / divisor;
      const vat = gross - net;
      return `
        <tr>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${e.note || 'Expense'}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px; text-transform: capitalize;">${e.category}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${e.date.split('T')[0]}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${cur}${gross.toFixed(2)}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${cur}${net.toFixed(2)}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #F1F5F9; font-size: 11px; font-weight: bold; color: #B91C1C;">${cur}${vat.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>${rate}% VAT Tax Summary Return - ${settings.companyName || 'AzuraOS'}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0F172A; line-height: 1.5; padding: 35px; max-width: 850px; margin: 0 auto; background: #FFF; }
            .no-print { background: #FFFBEB; border: 1px solid #FDE68A; padding: 12px 20px; border-radius: 8px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #78350F; }
            .header { border-bottom: 3px solid #0F172A; padding-bottom: 15px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
            .company { font-size: 22px; font-weight: 800; color: #0F172A; margin: 0; }
            .doc-title { font-size: 14px; font-weight: 700; text-transform: uppercase; color: #D97706; letter-spacing: 0.05em; margin-top: 2px; }
            .meta { font-size: 11px; color: #64748B; text-align: right; }
            .tax-badge { background: #FEF3C7; color: #92400E; border: 1px solid #FCD34D; padding: 2px 8px; border-radius: 4px; font-weight: 700; font-size: 10px; }
            
            .kpi-grid { display: grid; grid-template-cols: repeat(3, 1fr); gap: 15px; margin-bottom: 25px; }
            .kpi-card { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; padding: 15px; }
            .kpi-card-highlight { background: #1E293B; color: white; border-radius: 10px; padding: 15px; }
            .kpi-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #64748B; margin-bottom: 4px; }
            .kpi-card-highlight .kpi-label { color: #FCD34D; }
            .kpi-value { font-size: 20px; font-weight: 800; font-family: monospace; }
            .kpi-sub { font-size: 10px; margin-top: 4px; color: #64748B; }
            .kpi-card-highlight .kpi-sub { color: #94A3B8; }

            .section-header { font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #0F172A; border-bottom: 2px solid #E2E8F0; padding-bottom: 6px; margin: 25px 0 12px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 15px; }
            th { background: #F8FAFC; text-align: left; padding: 8px 12px; font-weight: 700; color: #475569; border-bottom: 2px solid #E2E8F0; }
            
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="no-print">
            <span>💡 <strong>Tax Filing Ready:</strong> Choose <strong>"Save as PDF"</strong> in your browser to download this official ${rate}% Tax Statement.</span>
            <button onclick="window.print();" style="background: #1E293B; color: white; border: none; padding: 6px 16px; border-radius: 6px; font-weight: bold; cursor: pointer;">Print / Save PDF</button>
          </div>

          <div class="header">
            <div>
              <h1 class="company">${settings.companyName || settings.senderName || 'Your Business Name'}</h1>
              <div class="doc-title">${rate}% Tax Filing Summary Report</div>
              <div style="font-size: 11px; color: #475569; margin-top: 4px;">
                Tax Reg Ref: <strong>${settings.taxNumber || 'Registered'}</strong> | Authority: <strong>${taxLabel}</strong> | Email: ${settings.email || 'N/A'}
              </div>
            </div>
            <div class="meta">
              <div><strong>Generated Date:</strong> ${dateStr}</div>
              <div style="margin-top: 4px;"><span class="tax-badge">${rate}% TAX RATE (${cur})</span></div>
            </div>
          </div>

          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-label">Output Tax (Collected)</div>
              <div class="kpi-value" style="color: #15803D;">${cur}${totalOutputVat.toFixed(2)}</div>
              <div class="kpi-sub">${rate}% on ${cur}${totalGrossRev.toFixed(2)} Revenue</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-label">Input Tax (Claimable)</div>
              <div class="kpi-value" style="color: #B91C1C;">${cur}${totalInputVat.toFixed(2)}</div>
              <div class="kpi-sub">${rate}% on ${cur}${totalGrossExp.toFixed(2)} Expenses</div>
            </div>
            <div class="kpi-card-highlight">
              <div class="kpi-label">Net Tax Payable (${taxLabel})</div>
              <div class="kpi-value" style="color: #FBBF24;">${cur}${netVatPayable >= 0 ? netVatPayable.toFixed(2) : `(${Math.abs(netVatPayable).toFixed(2)} Refund)`}</div>
              <div class="kpi-sub">${netVatPayable >= 0 ? 'Net Tax Amount Due' : 'Tax Authority Refund Outstanding'}</div>
            </div>
          </div>

          <div class="section-header">Output Tax Summary by Income Stream</div>
          <table>
            <thead>
              <tr>
                <th>Income Stream</th>
                <th>Gross Revenue</th>
                <th>Net (Excl. Tax)</th>
                <th>${rate}% Output Tax Portion</th>
              </tr>
            </thead>
            <tbody>
              ${streamTaxRows}
            </tbody>
          </table>

          <div class="section-header">Input Tax Summary by Expense Category</div>
          <table>
            <thead>
              <tr>
                <th>Expense Category</th>
                <th>Gross Expenses</th>
                <th>Net (Excl. Tax)</th>
                <th>${rate}% Input Tax Portion</th>
              </tr>
            </thead>
            <tbody>
              ${categoryTaxRows}
            </tbody>
          </table>

          <div class="section-header">Tax Audit Schedule - Recent Revenue Bookings</div>
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Stream</th>
                <th>Date</th>
                <th>Gross</th>
                <th>Net</th>
                <th>${rate}% Output Tax</th>
              </tr>
            </thead>
            <tbody>
              ${bookingDetailRows || '<tr><td colspan="6" style="padding:10px; color:#64748B;">No revenue bookings recorded in period.</td></tr>'}
            </tbody>
          </table>

          <div class="section-header">Tax Audit Schedule - Recent Expenses</div>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Category</th>
                <th>Date</th>
                <th>Gross</th>
                <th>Net</th>
                <th>${rate}% Input Tax</th>
              </tr>
            </thead>
            <tbody>
              ${expenseDetailRows || '<tr><td colspan="6" style="padding:10px; color:#64748B;">No expenses recorded in period.</td></tr>'}
            </tbody>
          </table>

          <footer style="margin-top: 40px; border-top: 1px solid #E2E8F0; padding-top: 12px; font-size: 10px; color: #94A3B8; text-align: center;">
            Generated via AzuraOS Sovereign Operating System • Tax calculations derived using Net = Gross / (1 + ${rate}/100) and Tax = Gross - Net.
          </footer>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 600);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setDownloading(null);
  };

  // Generate beautiful, high-fidelity Printable report
  // Opens in browser's print engine so they can instantly select "Save as PDF"
  const handlePrintPDFSummary = () => {
    setDownloading('summary_pdf');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Could not open print window. Please allow popups for AzuraOS.');
      setDownloading(null);
      return;
    }

    const activeBookings = bookings.filter(b => b.status !== 'cancelled');
    const totalIncome = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const margin = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0;
    const marginStatus = margin >= threshold ? 'HEALTHY (SAFE)' : 'ACTION REQUIRED (RISK)';

    // Build Stream Profitability Rows
    const streamProfitRows = streams.map(stream => {
      const inc = activeBookings.filter(b => b.type === stream).reduce((sum, b) => sum + (Number(b.price) || 0), 0);
      const exp = expenses.filter(e => e.linked_stream === stream).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      const profit = inc - exp;
      const stMargin = inc > 0 ? (profit / inc) * 100 : 0;
      const marginColor = stMargin >= threshold ? '#14B8A6' : '#F43F5E';
      return `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #E2E8F0; font-weight: bold; text-transform: capitalize;">${stream}</td>
          <td style="padding: 10px; border-bottom: 1px solid #E2E8F0;">R${inc.toFixed(2)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #E2E8F0;">R${exp.toFixed(2)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #E2E8F0; font-weight: bold; color: ${marginColor};">${stMargin.toFixed(1)}%</td>
        </tr>
      `;
    }).join('');

    // Build J-Lens Rows
    const jLensRows = insights.map(ins => {
      const linkLabel = ins.linked_type !== 'general' ? `Linked to ${ins.linked_type}` : 'General';
      return `
        <div style="background-color: #F8FAFC; border: 1px solid #E2E8F0; border-left: 4px solid #C8A27C; padding: 15px; margin-bottom: 15px; border-radius: 8px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <strong style="color: #1E293B; font-size: 14px;">${ins.title}</strong>
            <span style="font-size: 11px; color: #64748B;">${new Date(ins.created_at).toLocaleDateString()}</span>
          </div>
          <p style="font-size: 13px; color: #334155; margin: 0; font-style: italic; leading-height: 1.5;">"${ins.note}"</p>
          <span style="display: inline-block; font-size: 10px; background-color: #E2E8F0; color: #475569; padding: 2px 8px; border-radius: 4px; margin-top: 8px; font-weight: bold; text-transform: uppercase;">
            ${linkLabel}
          </span>
        </div>
      `;
    }).join('') || '<p style="color: #64748B; font-style: italic; font-size: 12px;">No strategic insights logged in J-Lens yet.</p>';

    printWindow.document.write(`
      <html>
        <head>
          <title>AzuraOS Executive Insight Summary - ${new Date().toISOString().split('T')[0]}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              color: #1E293B;
              line-height: 1.5;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              border-bottom: 3px solid #0F172A;
              padding-bottom: 20px;
              margin-bottom: 30px;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
            }
            .title {
              font-size: 26px;
              font-weight: 800;
              letter-spacing: -0.05em;
              color: #0F172A;
              margin: 0 0 4px 0;
            }
            .subtitle {
              font-size: 12px;
              color: #64748B;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.1em;
              margin: 0;
            }
            .meta-date {
              font-size: 12px;
              color: #475569;
              font-weight: 600;
            }
            .grid-stats {
              display: grid;
              grid-template-cols: repeat(3, 1fr);
              gap: 20px;
              margin-bottom: 30px;
            }
            .stat-card {
              background: #F1F5F9;
              border-radius: 12px;
              padding: 20px;
              border: 1px solid #E2E8F0;
            }
            .stat-card-accent {
              background: #0F172A;
              color: white;
              border-radius: 12px;
              padding: 20px;
            }
            .stat-label {
              font-size: 11px;
              text-transform: uppercase;
              font-weight: 700;
              letter-spacing: 0.05em;
              color: #64748B;
              margin-bottom: 6px;
            }
            .stat-card-accent .stat-label {
              color: #94A3B8;
            }
            .stat-value {
              font-size: 24px;
              font-weight: 800;
              font-family: monospace;
            }
            .section {
              margin-bottom: 35px;
            }
            .section-title {
              font-size: 16px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              border-bottom: 2px solid #E2E8F0;
              padding-bottom: 8px;
              margin-bottom: 15px;
              color: #0F172A;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            th {
              background-color: #F8FAFC;
              text-align: left;
              padding: 10px;
              font-weight: 700;
              color: #475569;
              border-bottom: 2px solid #E2E8F0;
            }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="background: #FFFBEB; border: 1px solid #FDE68A; padding: 12px 20px; border-radius: 8px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #78350F;">
            <span>💡 <strong>Tip:</strong> Choose <strong>"Save as PDF"</strong> in your browser's printer selection to save this report locally.</span>
            <button onclick="window.print();" style="background: #1E293B; color: white; border: none; padding: 6px 16px; border-radius: 6px; font-weight: bold; cursor: pointer;">Print / Save PDF</button>
          </div>

          <div class="header">
            <div>
              <h1 class="title">AzuraOS</h1>
              <p class="subtitle">Founder Operating System — Executive Financial & J-Lens Report</p>
            </div>
            <div class="meta-date">
              Report Generated: ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          <div class="grid-stats">
            <div class="stat-card">
              <div class="stat-label">Total Sandbox Revenue</div>
              <div class="stat-value" style="color: #0F172A;">R${totalIncome.toFixed(2)}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Total Operational Expenses</div>
              <div class="stat-value" style="color: #64748B;">R${totalExpenses.toFixed(2)}</div>
            </div>
            <div class="stat-card-accent">
              <div class="stat-label" style="color: #38BDF8;">Live Sandbox Margin</div>
              <div class="stat-value" style="color: #38BDF8;">${margin.toFixed(1)}%</div>
              <div style="font-size: 9px; font-weight: bold; margin-top: 4px; text-transform: uppercase;">
                Threshold Status: ${marginStatus}
              </div>
            </div>
          </div>

          <div class="section">
            <h2 class="section-title">Income Stream Profitability Breakdown</h2>
            <table>
              <thead>
                <tr>
                  <th style="width: 40%">Stream Type</th>
                  <th style="width: 20%">Total Revenue</th>
                  <th style="width: 20%">Expenses Linked</th>
                  <th style="width: 20%">Net Margin</th>
                </tr>
              </thead>
              <tbody>
                ${streamProfitRows}
              </tbody>
            </table>
          </div>

          <div class="section" style="page-break-before: auto;">
            <h2 class="section-title">🧠 J-Lens Strategic Insights</h2>
            <div>
              ${jLensRows}
            </div>
          </div>

          <footer style="margin-top: 50px; border-top: 1px solid #E2E8F0; padding-top: 15px; font-size: 11px; color: #94A3B8; text-align: center;">
            Sovereign Operating System sandbox records • Confidential & Proprietary
          </footer>

          <script>
            // Auto trigger print dialogue shortly after parsing is complete
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 600);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setDownloading(null);
  };

  // Dedicated Professional Bookings & Expenses PDF Summary Report Generator
  const handleDownloadBookingsExpensesPDFReport = () => {
    setDownloading('bookings_expenses_pdf');
    recordBackupAction();
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Could not open print window. Please allow popups for your browser.');
      setDownloading(null);
      return;
    }

    const settings = getBusinessSettings();
    const cur = settings.currencySymbol || 'R';
    const company = settings.companyName || settings.senderName || 'Workspace Business HQ';
    const email = settings.email || '';
    const phone = settings.phone || '';
    const taxNum = settings.taxNumber || '';
    const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    // Audit Log recording
    logAuditAction({
      actionType: 'UPDATE_SETTINGS',
      entityType: 'System',
      details: `Generated and downloaded Bookings & Expenses Executive Summary PDF Report (${bookings.length} bookings, ${expenses.length} expenses)`
    });

    // Key metrics calculations
    const activeBookings = bookings.filter(b => b.status !== 'cancelled');
    const totalRevenue = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const netProfit = totalRevenue - totalExpenses;
    const margin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    const marginStatus = margin >= threshold ? 'HEALTHY (SAFE)' : 'BELOW TARGET THRESHOLD';

    // Status breakdown
    const paidBookings = bookings.filter(b => b.status === 'paid');
    const unpaidBookings = bookings.filter(b => (b.status || 'unpaid') === 'unpaid');
    const paidRevenue = paidBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);

    // Stream Breakdown
    const streamRows = streams.map(stream => {
      const streamBookings = activeBookings.filter(b => b.type === stream);
      const inc = streamBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
      const exp = expenses.filter(e => e.linked_stream === stream).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      const streamNet = inc - exp;
      const share = totalRevenue > 0 ? ((inc / totalRevenue) * 100).toFixed(1) : '0.0';
      return `
        <tr>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-weight: 600; text-transform: capitalize;">${stream}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace;">${streamBookings.length}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace; font-weight: 600;">${cur}${inc.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace;">${share}%</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace; color: #DC2626;">${cur}${exp.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace; font-weight: 700; color: ${streamNet >= 0 ? '#16A34A' : '#DC2626'};">${cur}${streamNet.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    // Expense Category Breakdown
    const categories: ExpenseCategory[] = ['travel', 'supplies', 'rent', 'other'];
    const categoryRows = categories.map(cat => {
      const catExpenses = expenses.filter(e => e.category === cat);
      const sum = catExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const share = totalExpenses > 0 ? ((sum / totalExpenses) * 100).toFixed(1) : '0.0';
      return `
        <tr>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-weight: 600; text-transform: capitalize;">${cat}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace;">${catExpenses.length} items</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace; font-weight: 600; color: #B91C1C;">${cur}${sum.toFixed(2)}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #E2E8F0; font-family: monospace;">${share}%</td>
        </tr>
      `;
    }).join('');

    // Bookings Detail Rows
    const bookingDetailRows = bookings.map(b => {
      const isPaid = b.status === 'paid';
      const isCancelled = b.status === 'cancelled';
      const statusBadge = isCancelled
        ? `<span style="background:#FFE4E6; color:#BE123C; border:1px solid #FDA4AF; padding:2px 8px; border-radius:4px; font-weight:700; font-size:10px; text-transform:uppercase;">CANCELLED</span>`
        : isPaid 
        ? `<span style="background:#DCFCE7; color:#15803D; border:1px solid #86EFAC; padding:2px 8px; border-radius:4px; font-weight:700; font-size:10px; text-transform:uppercase;">PAID</span>`
        : `<span style="background:#FEF3C7; color:#B45309; border:1px solid #FDE68A; padding:2px 8px; border-radius:4px; font-weight:700; font-size:10px; text-transform:uppercase;">UNPAID</span>`;
      
      const bDate = b.date ? new Date(b.date).toLocaleDateString() : 'N/A';
      return `
        <tr style="${isCancelled ? 'opacity: 0.6; text-decoration: line-through;' : ''}">
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-weight: 600; color: #1E293B;">${b.client_name || 'Client'}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; text-transform: capitalize; font-size: 11px;">${b.type}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-size: 11px; font-family: monospace; color: #64748B;">${b.invoice_number || 'N/A'}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${bDate}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9;">${statusBadge}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-weight: 700; font-family: monospace; text-align: right; color: ${isCancelled ? '#94A3B8' : '#0F172A'};">${cur}${b.price.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    // Expenses Detail Rows
    const expenseDetailRows = expenses.map(e => {
      const eDate = e.date ? new Date(e.date).toLocaleDateString() : 'N/A';
      return `
        <tr>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-weight: 600; color: #1E293B;">${e.note || 'Expense Item'}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; text-transform: capitalize; font-size: 11px;">${e.category}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; text-transform: capitalize; font-size: 11px; color: #64748B;">${e.linked_stream}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-size: 11px;">${eDate}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #F1F5F9; font-weight: 700; font-family: monospace; text-align: right; color: #B91C1C;">${cur}${e.amount.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Executive Bookings & Expenses Summary Report - ${company}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              color: #0F172A;
              background-color: #FFFFFF;
              line-height: 1.5;
              padding: 36px;
              max-width: 900px;
              margin: 0 auto;
            }
            .no-print {
              background: #EFF6FF;
              border: 1px solid #BFDBFE;
              padding: 14px 20px;
              border-radius: 12px;
              margin-bottom: 28px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 13px;
              color: #1E40AF;
            }
            .no-print button {
              background: #2563EB;
              color: #FFFFFF;
              border: none;
              padding: 8px 20px;
              border-radius: 8px;
              font-weight: 700;
              font-size: 12px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .no-print button:hover {
              background: #1D4ED8;
            }
            .header-bar {
              border-bottom: 3px solid #0F172A;
              padding-bottom: 20px;
              margin-bottom: 28px;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
            }
            .company-name {
              font-size: 24px;
              font-weight: 800;
              color: #0F172A;
              letter-spacing: -0.03em;
              margin: 0;
            }
            .report-title {
              font-size: 13px;
              font-weight: 800;
              text-transform: uppercase;
              color: #2563EB;
              letter-spacing: 0.08em;
              margin-top: 4px;
            }
            .meta-info {
              font-size: 11px;
              color: #64748B;
              text-align: right;
            }
            .kpi-grid {
              display: grid;
              grid-template-cols: repeat(4, 1fr);
              gap: 12px;
              margin-bottom: 28px;
            }
            .kpi-card {
              background: #F8FAFC;
              border: 1px solid #E2E8F0;
              border-radius: 12px;
              padding: 14px;
            }
            .kpi-card-dark {
              background: #0F172A;
              color: white;
              border-radius: 12px;
              padding: 14px;
            }
            .kpi-label {
              font-size: 10px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              color: #64748B;
              margin-bottom: 4px;
            }
            .kpi-card-dark .kpi-label {
              color: #94A3B8;
            }
            .kpi-val {
              font-size: 20px;
              font-weight: 800;
              font-family: monospace;
            }
            .kpi-sub {
              font-size: 10px;
              margin-top: 4px;
              color: #64748B;
            }
            .kpi-card-dark .kpi-sub {
              color: #CBD5E1;
            }
            .section-title {
              font-size: 13px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              color: #0F172A;
              border-bottom: 2px solid #E2E8F0;
              padding-bottom: 6px;
              margin: 28px 0 14px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
              margin-bottom: 20px;
            }
            th {
              background: #F8FAFC;
              text-align: left;
              padding: 8px 12px;
              font-weight: 700;
              color: #475569;
              border-bottom: 2px solid #CBD5E1;
            }
            .totals-row td {
              font-weight: 800;
              border-top: 2px solid #0F172A;
              background: #F8FAFC;
              padding: 10px 12px;
            }
            footer {
              margin-top: 40px;
              border-top: 1px solid #E2E8F0;
              padding-top: 14px;
              font-size: 10px;
              color: #94A3B8;
              text-align: center;
            }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          <div class="no-print">
            <span>📄 <strong>PDF Export Ready:</strong> Click <strong>"Print / Save PDF"</strong> and select <strong>"Save as PDF"</strong> in your browser printer window.</span>
            <button onclick="window.print();">Print / Save PDF</button>
          </div>

          <div class="header-bar">
            <div>
              <h1 class="company-name">${company}</h1>
              <div class="report-title">Bookings & Expenses Summary Report</div>
              <div style="font-size: 11px; color: #475569; margin-top: 4px;">
                ${email ? `Email: ${email} | ` : ''} ${phone ? `Phone: ${phone} | ` : ''} ${taxNum ? `Tax Ref: ${taxNum}` : ''}
              </div>
            </div>
            <div class="meta-info">
              <div><strong>Generated Date:</strong> ${dateStr}</div>
              <div style="margin-top: 4px;">Scope: <strong>${bookings.length} Bookings</strong> &bull; <strong>${expenses.length} Expenses</strong></div>
            </div>
          </div>

          <!-- KPI Summary Cards -->
          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-label">Gross Revenue</div>
              <div class="kpi-val" style="color: #16A34A;">${cur}${totalRevenue.toFixed(2)}</div>
              <div class="kpi-sub">${paidBookings.length} Paid (${cur}${paidRevenue.toFixed(0)}) &bull; ${unpaidBookings.length} Unpaid</div>
            </div>

            <div class="kpi-card">
              <div class="kpi-label">Total Expenses</div>
              <div class="kpi-val" style="color: #DC2626;">${cur}${totalExpenses.toFixed(2)}</div>
              <div class="kpi-sub">${expenses.length} Operational Entries</div>
            </div>

            <div class="kpi-card-dark">
              <div class="kpi-label">Net Operating Income</div>
              <div class="kpi-val" style="color: ${netProfit >= 0 ? '#4ADE80' : '#F87171'};">${cur}${netProfit.toFixed(2)}</div>
              <div class="kpi-sub">Cash Flow Net Cash Position</div>
            </div>

            <div class="kpi-card">
              <div class="kpi-label">Operating Margin</div>
              <div class="kpi-val" style="color: #2563EB;">${margin.toFixed(1)}%</div>
              <div class="kpi-sub">Status: <strong>${marginStatus}</strong></div>
            </div>
          </div>

          <!-- Income Stream Performance -->
          <div class="section-title">Income Stream Revenue & Margin Performance</div>
          <table>
            <thead>
              <tr>
                <th>Stream Name</th>
                <th>Bookings Count</th>
                <th>Gross Income</th>
                <th>% Share</th>
                <th>Linked Expenses</th>
                <th>Stream Net</th>
              </tr>
            </thead>
            <tbody>
              ${streamRows || '<tr><td colspan="6" style="padding:10px; color:#64748B;">No income streams configured.</td></tr>'}
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td>Total Across Streams</td>
                <td style="font-family: monospace;">${bookings.length}</td>
                <td style="font-family: monospace; color: #16A34A;">${cur}${totalRevenue.toFixed(2)}</td>
                <td style="font-family: monospace;">100%</td>
                <td style="font-family: monospace; color: #DC2626;">${cur}${totalExpenses.toFixed(2)}</td>
                <td style="font-family: monospace; color: ${netProfit >= 0 ? '#16A34A' : '#DC2626'};">${cur}${netProfit.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

          <!-- Expense Category Breakdown -->
          <div class="section-title">Expense Breakdown by Category</div>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Item Count</th>
                <th>Total Amount</th>
                <th>% Share of Expenses</th>
              </tr>
            </thead>
            <tbody>
              ${categoryRows || '<tr><td colspan="4" style="padding:10px; color:#64748B;">No expense categories.</td></tr>'}
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td>Total Expenses</td>
                <td style="font-family: monospace;">${expenses.length} items</td>
                <td style="font-family: monospace; color: #DC2626;">${cur}${totalExpenses.toFixed(2)}</td>
                <td style="font-family: monospace;">100%</td>
              </tr>
            </tfoot>
          </table>

          <!-- Detailed Itemized Bookings Ledger -->
          <div class="section-title">Itemized Bookings Ledger (${bookings.length} Records)</div>
          <table>
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Income Stream</th>
                <th>Invoice #</th>
                <th>Booking Date</th>
                <th>Status</th>
                <th style="text-align: right;">Gross Price</th>
              </tr>
            </thead>
            <tbody>
              ${bookingDetailRows || '<tr><td colspan="6" style="padding:10px; color:#64748B; text-align:center;">No bookings recorded yet.</td></tr>'}
            </tbody>
          </table>

          <!-- Detailed Itemized Expenses Ledger -->
          <div class="section-title page-break">Itemized Expenses Ledger (${expenses.length} Records)</div>
          <table>
            <thead>
              <tr>
                <th>Description / Note</th>
                <th>Category</th>
                <th>Linked Stream</th>
                <th>Expense Date</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${expenseDetailRows || '<tr><td colspan="5" style="padding:10px; color:#64748B; text-align:center;">No expenses recorded yet.</td></tr>'}
            </tbody>
          </table>

          <footer>
            Report generated via AzuraOS Executive HQ &bull; Confidential & Proprietary &bull; ${new Date().getFullYear()} ${company}
          </footer>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setDownloading(null);
  };

  return (
    <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <Download size={18} className="text-[#14B8A6]" /> Export & Download Center
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Export secure backup summaries of your income streams, cash flow metrics, and J-Lens journal logs.
          </p>
        </div>

        {/* Backup Health Status Badge */}
        <div className="flex items-center gap-2.5 px-3.5 py-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl text-xs">
          {backupStatus.isOverdue ? (
            <ShieldAlert size={18} className="text-amber-400 shrink-0 animate-pulse" />
          ) : (
            <ShieldCheck size={18} className="text-emerald-400 shrink-0" />
          )}
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Backup Health (7-Day Target)</div>
            <div className={`font-bold ${backupStatus.isOverdue ? 'text-amber-400' : 'text-emerald-400'}`}>
              {backupStatus.lastBackupDate 
                ? `Last backed up ${backupStatus.daysSinceLastBackup === 0 ? 'today' : `${backupStatus.daysSinceLastBackup}d ago`}`
                : 'No recent export recorded'}
            </div>
          </div>
        </div>
      </div>

      {/* Featured AI Automation: Automated Statement & Receipt Reconciler */}
      {onOpenReconciler && (
        <div className="bg-gradient-to-r from-teal-950/40 via-[#0C0E0F] to-cyan-950/30 border border-teal-500/30 hover:border-teal-500/60 rounded-2xl p-5 transition-all shadow-lg relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-teal-500/15 text-teal-400 border border-teal-500/30 flex items-center justify-center shrink-0 mt-0.5 shadow-inner">
                <Sparkles size={22} className="animate-pulse" />
              </div>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-base font-bold text-white">Automated Bank Statement &amp; Receipt Reconciler</h4>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider bg-teal-500/20 text-teal-300 border border-teal-500/40 rounded-md shrink-0">
                    ZERO MANUAL ENTRY
                  </span>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Drop in your monthly bank statements (PDF, CSV, OFX, TXT), paste online banking rows directly, or upload receipt &amp; invoice photos. Gemini AI categorizes vendors, parses amounts, and reconciles transactions in seconds.
                </p>
              </div>
            </div>

            <button
              onClick={onOpenReconciler}
              className="w-full md:w-auto px-6 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/25 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
            >
              <Sparkles size={16} />
              <span>Launch AI Reconciler</span>
            </button>
          </div>
        </div>
      )}

      {/* Featured Primary Action: Download Report as PDF */}
      <div className="bg-[#0C0E0F] border border-blue-500/30 hover:border-blue-500/60 rounded-xl p-5 transition-all shadow-lg">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/30 flex items-center justify-center shrink-0 mt-0.5 shadow-inner">
              <FileText size={22} />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-base font-bold text-white">Download Report as PDF</h4>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/30 rounded-md shrink-0">
                  EXECUTIVE SUMMARY
                </span>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Generates an audit-ready, professional PDF report summarizing all current bookings ({bookings.length}), operational expenses ({expenses.length}), revenue stream breakdown, and net profit margin.
              </p>
            </div>
          </div>

          <button
            onClick={handleDownloadBookingsExpensesPDFReport}
            disabled={downloading !== null}
            className="w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50 shrink-0"
          >
            {downloading === 'bookings_expenses_pdf' ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Preparing PDF Report...</span>
              </>
            ) : (
              <>
                <Download size={16} />
                <span>Download Report as PDF</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Standard Export Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-2">
        {/* Bookings CSV */}
        <button
          onClick={exportBookingsToCSV}
          disabled={downloading !== null}
          className="flex flex-col items-center justify-center p-4 bg-[#0C0E0F] hover:bg-[#14B8A6]/5 border border-[#1F2426] hover:border-[#14B8A6]/30 rounded-xl transition-all cursor-pointer group text-center gap-2 focus:outline-none focus:ring-1 focus:ring-[#14B8A6]"
        >
          <div className="w-10 h-10 rounded-lg bg-teal-500/10 text-teal-400 flex items-center justify-center group-hover:scale-105 transition-transform shrink-0">
            <Calendar size={18} />
          </div>
          <div className="space-y-0.5">
            <span className="text-xs font-semibold text-white block">Bookings</span>
            <span className="text-[10px] text-gray-500 block">Export as CSV</span>
          </div>
        </button>

        {/* Expenses CSV */}
        <button
          onClick={exportExpensesToCSV}
          disabled={downloading !== null}
          className="flex flex-col items-center justify-center p-4 bg-[#0C0E0F] hover:bg-[#14B8A6]/5 border border-[#1F2426] hover:border-[#14B8A6]/30 rounded-xl transition-all cursor-pointer group text-center gap-2 focus:outline-none focus:ring-1 focus:ring-[#14B8A6]"
        >
          <div className="w-10 h-10 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center group-hover:scale-105 transition-transform shrink-0">
            <DollarSign size={18} />
          </div>
          <div className="space-y-0.5">
            <span className="text-xs font-semibold text-white block">Expenses</span>
            <span className="text-[10px] text-gray-500 block">Export as CSV</span>
          </div>
        </button>

        {/* J-Lens CSV */}
        <button
          onClick={exportJLensToCSV}
          disabled={downloading !== null}
          className="flex flex-col items-center justify-center p-4 bg-[#0C0E0F] hover:bg-gold-accent/5 border border-[#1F2426] hover:border-gold-accent/30 rounded-xl transition-all cursor-pointer group text-center gap-2 focus:outline-none focus:ring-1 focus:ring-gold-accent"
        >
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-gold-accent flex items-center justify-center group-hover:scale-105 transition-transform shrink-0">
            <Brain size={18} />
          </div>
          <div className="space-y-0.5">
            <span className="text-xs font-semibold text-white block">J-Lens Notes</span>
            <span className="text-[10px] text-gray-500 block">Export as CSV</span>
          </div>
        </button>

        {/* PDF Executive Report */}
        <button
          onClick={handleDownloadBookingsExpensesPDFReport}
          disabled={downloading !== null}
          className="flex flex-col items-center justify-center p-4 bg-[#0C0E0F] hover:bg-blue-500/5 border border-[#1F2426] hover:border-blue-500/30 rounded-xl transition-all cursor-pointer group text-center gap-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center group-hover:scale-105 transition-transform shrink-0">
            <FileText size={18} />
          </div>
          <div className="space-y-0.5">
            <span className="text-xs font-semibold text-white block">Summary Report</span>
            <span className="text-[10px] text-gray-500 block">Download as PDF</span>
          </div>
        </button>
      </div>

      {/* Featured Full Workspace JSON Backup Card */}
      <div className="bg-[#0C0E0F] border border-[#14B8A6]/30 hover:border-[#14B8A6]/60 rounded-xl p-4 transition-all">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/30 flex items-center justify-center shrink-0 mt-0.5">
              <Database size={20} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-sm font-bold text-white">Full Workspace Backup (.json)</h4>
                <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/30 rounded-md shrink-0">
                  Offline Copy
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                Generates a single JSON file containing all app data: bookings ({bookings.length}), expenses ({expenses.length}), J-Lens insights ({insights.length}), revenue streams ({streams.length}), and company tax profile.
              </p>
            </div>
          </div>

          <button
            onClick={exportFullBackupJSON}
            disabled={downloading !== null}
            className="w-full sm:w-auto px-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/10 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50 shrink-0"
          >
            <FileJson size={16} />
            <span>Download Full Backup</span>
          </button>
        </div>
      </div>

      {/* Featured VAT / Tax Summary Section */}
      {(() => {
        const settings = getBusinessSettings();
        const rate = settings.vatRate !== undefined ? settings.vatRate : 15;
        const cur = settings.currencySymbol || 'R';
        const taxLabel = settings.taxAuthorityLabel || 'Tax Authority (e.g. SARS, IRS)';

        return (
          <div className="bg-[#0C0E0F] border border-amber-500/25 hover:border-amber-500/40 rounded-xl p-4 transition-all">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Receipt size={20} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-bold text-white">{rate}% Tax Filing Summary Report ({cur})</h4>
                    <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-md shrink-0">
                      Tax Filing Ready
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                    Calculates {rate}% Output Tax collected on revenue and {rate}% Input Tax claimable on expenses in {cur}. Generates an audit-ready tax schedule for tax authorities ({taxLabel}).
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-[#1F2426]">
                <button
                  onClick={exportTaxSummaryCSV}
                  disabled={downloading !== null}
                  className="flex-1 md:flex-none px-3.5 py-2.5 bg-[#151819] hover:bg-[#1F2426] border border-[#2A2F31] hover:border-amber-500/40 text-amber-300 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 disabled:opacity-50"
                >
                  <FileSpreadsheet size={15} className="text-amber-400" />
                  <span>Tax CSV</span>
                </button>
                <button
                  onClick={handlePrintTaxPDFSummary}
                  disabled={downloading !== null}
                  className="flex-1 md:flex-none px-4 py-2.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 disabled:opacity-50"
                >
                  <FileText size={15} className="text-amber-400" />
                  <span>Tax Return PDF</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export const ExportCenter = React.memo(ExportCenterComponent);
